import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import { Marker } from "react-leaflet";

// SKYCORE MAP.NAV.1 — marqueur drone UNIVERSEL (LiveMap, Missions, cockpit, mini-carte, relecture).
// Ancré EXACTEMENT sur la position GPS (centre de l'icône). Flèche principale = avant du drone (cap
// Pixhawk) ; indicateur secondaire fuchsia, distinct = direction réelle du déplacement (COG), plein si
// MESURÉE, pointillé si DÉRIVÉE, absent si inconnue/stationnaire. Cap inconnu -> symbole neutre.
// Télémétrie périmée -> style dégradé. Le symbole tourne avec la carte : son orientation visuelle
// finale est cap − rotation carte (aucune contre-rotation, donc aucune double rotation).
export const COG_COLOR = "#E879F9";
const STALE_COLOR = "#737373";
const SIZE = 48;

export function markerState(nav) {
  if (!nav || !nav.positionValid) return "NO_POSITION";
  if (nav.freshness !== "FRESH" && nav.freshness !== "REPLAY") return "STALE";
  return nav.headingValid ? "HEADING" : "NEUTRAL";
}

const r1 = (v) => Math.round(v * 10) / 10;

export function droneMarkerHtml(nav, { color = "#22C55E", selected = false, active = false, id = "drone" } = {}) {
  const state = markerState(nav);
  const stale = state === "STALE";
  const c = stale ? STALE_COLOR : color;
  const course = !stale && nav && nav.courseValid ? r1(nav.courseDeg) : null;
  const heading = state === "HEADING" ? r1(nav.headingDeg) : null;
  const ring = selected
    ? `<circle r="21" fill="none" stroke="${c}" stroke-width="2" opacity="0.9"/>`
    : `<circle r="21" fill="none" stroke="${c}" stroke-width="1" opacity="0.35"/>`;
  const cog = course == null ? "" : `<g data-cog="1" transform="rotate(${course})">
      <line x1="0" y1="-12" x2="0" y2="-22" stroke="${COG_COLOR}" stroke-width="3" ${nav.courseSource === "DERIVED" ? 'stroke-dasharray="3 2"' : ""}/>
      <path d="M0,-30 L6,-21 L-6,-21 Z" fill="${COG_COLOR}" stroke="#0A0A0A" stroke-width="1"/></g>`;
  const body = heading != null
    ? `<g data-heading="1" transform="rotate(${heading})"><path d="M0,-14 L9,10 L0,5 L-9,10 Z" fill="${c}" stroke="#0A0A0A" stroke-width="1.6" stroke-linejoin="round"/></g>`
    : `<circle r="7.5" fill="${c}33" stroke="${c}" stroke-width="2" ${stale ? 'stroke-dasharray="3 2"' : ""}/>
       <circle r="2" fill="${c}"/>`;
  return `<div class="drone-nav ${active && !stale ? "pulse-soft" : ""}" data-testid="drone-nav-marker-${id}" data-state="${state}"`
    + ` data-heading="${heading == null ? "" : heading}" data-course="${course == null ? "" : course}" data-course-source="${course == null ? "UNKNOWN" : nav.courseSource}"`
    + ` data-course-quality="${course == null ? "" : nav.courseQuality || ""}"`
    + ` style="width:${SIZE}px;height:${SIZE}px;${stale ? "opacity:0.55;" : ""}">`
    + `<svg width="${SIZE}" height="${SIZE}" viewBox="-24 -24 48 48" overflow="visible">${ring}${cog}${body}</svg></div>`;
}

const ICONS = new Map();
export function droneNavIcon(nav, opts) {
  const html = droneMarkerHtml(nav, opts);
  let icon = ICONS.get(html);
  if (!icon) {
    if (ICONS.size > 400) ICONS.clear();
    icon = L.divIcon({ className: "drone-marker", html, iconSize: [SIZE, SIZE], iconAnchor: [SIZE / 2, SIZE / 2] });
    ICONS.set(html, icon);
  }
  return icon;
}

// MAP.NAV.3 — mise à jour EN PLACE du symbole : la racine .drone-nav (cible des clics/taps, le SVG est
// pointer-events:none) est conservée ; seuls ses attributs et son contenu changent. Un setIcon à chaque
// télémétrie remplaçait l'élément et pouvait perdre un clic/tap commencé sur l'ancien.
export function patchMarkerElement(iconEl, html) {
  const root = iconEl && iconEl.firstElementChild;
  if (!root) return false;
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  const next = t.content.firstElementChild;
  if (!next) return false;
  Array.from(root.attributes).forEach((a) => { if (!next.hasAttribute(a.name)) root.removeAttribute(a.name); });
  Array.from(next.attributes).forEach((a) => { if (root.getAttribute(a.name) !== a.value) root.setAttribute(a.name, a.value); });
  root.replaceChildren(...Array.from(next.childNodes));
  return true;
}

export default function DroneNavigationMarker({ nav, color, selected, active, id, onClick, zIndexOffset, children }) {
  const valid = !!(nav && nav.positionValid);
  const html = valid ? droneMarkerHtml(nav, { color, selected, active, id }) : null;
  const iconRef = useRef(null);
  const markerRef = useRef(null);
  if (html && !iconRef.current) iconRef.current = L.divIcon({ className: "drone-marker", html, iconSize: [SIZE, SIZE], iconAnchor: [SIZE / 2, SIZE / 2] });
  const clickRef = useRef(onClick);
  clickRef.current = onClick;
  const handlers = useMemo(() => ({ click: (e) => clickRef.current && clickRef.current(e) }), []);
  useEffect(() => {
    if (!html || !iconRef.current) return;
    iconRef.current.options.html = html;
    const m = markerRef.current;
    patchMarkerElement(m && m.getElement(), html);
  }, [html]);
  if (!valid) return null;
  return (
    <Marker ref={markerRef} position={[nav.lat, nav.lng]} icon={iconRef.current} zIndexOffset={zIndexOffset}
      eventHandlers={onClick ? handlers : undefined}>
      {children}
    </Marker>
  );
}
